import { useState } from "react";

import {
    Buildings,
    CloseCircle,
    Document,
    CheckCircle,
    Upload,
    History,
    Hashtag,
    UserRounded,
    ClockCircle,
    Pen,
    Restart,
    Wallet,
    CaseMinimalistic,
} from "@solar-icons/react/ssr";
import { useTheme, useMediaQuery, Drawer } from "@mui/material";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { format, formatDistanceToNow } from "date-fns";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { request } from "@/Api";
import {
    selectHasRole,
    selectIsBaseOrg,
    selectPersonId,
} from "@/auth/authSlice";
import {
    PERMISSION_VIEW_CONFIDENTIAL_DATA,
    PERMISSION_APPROVE_BIDS,
    PERMISSION_CREATE_GLOBAL_BID_TEMPLATES,
} from "@/auth/constants";
import useEntitlements from "@/auth/useEntitlements";
import { InternalLink } from "@/common/CommonComponents";
import ConfirmationDialog from "@/common/ConfirmationDialog";
import FilesUploader from "@/common/FilesUploader";
import {
    FEATURE_BID_XODOSIGN,
    FEATURE_BID_APPROVAL_FLOW,
    FEATURE_MULTI_LEGAL_ENTITY,
} from "@/constants";
import BidActionPopup from "@/sheet/BidActionPopup";
import BidInsights from "@/sheet/BidInsights";
import { getReadOnly, getIsTemplate, store } from "@/sheet/bidStore";
import BidTemplatePublishingSwitch from "@/sheet/BidTemplatePublishingSwitch";
import BidTitle from "@/sheet/BidTitle";
import * as constants from "@/sheet/constants";
import { downloadTopsheet } from "@/sheet/TopsheetDownload";
import { BidHistory } from "./BidHistory";
import TopSheetButton from "./TopSheetButton";

const CustomBox = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30%;
    min-width: 500px;
    padding: 24px;
`;
const IconText = styled.span`
    display: inline-block;
    vertical-align: top;
    padding: 5px 0 0 5px;
    font-size: 12px;
`;

export default function BidHeader({
    bid: paramBid,
    timestampsOpen,
    setTimestampsOpen,
    openRequestForm,
    openTopsheetConfig,
    refreshBid,
    getBid,
    isRunning,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [actionOpen, setActionOpen] = useState(false);
    const [actionType, setActionType] = useState("");
    const [cancelOpen, setCancelOpen] = useState(false);
    const [downloadForSignatureOpen, setDownloadForSignatureOpen] =
        useState(false);
    const [confirmingSigned, setConfirmingSigned] = useState(false);
    const [signedOpen, setSignedOpen] = useState(false);
    const [filesUploaderOpen, setFilesUploaderOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [note, setNote] = useState("");

    const isFinance = useSelector((state) => selectHasRole(state, "finance"));
    const personId = useSelector(selectPersonId);
    const isBaseOrg = useSelector(selectIsBaseOrg);

    const { hasFeature, hasPermission } = useEntitlements();

    const canViewConfidentialData = hasPermission(
        PERMISSION_VIEW_CONFIDENTIAL_DATA,
    );
    const canApproveBids = hasPermission(PERMISSION_APPROVE_BIDS);
    const hasBidApprovalFlow = hasFeature(FEATURE_BID_APPROVAL_FLOW);
    const canCreateGlobalTemplates = hasPermission(
        PERMISSION_CREATE_GLOBAL_BID_TEMPLATES,
    );

    const activeBid = paramBid || store.bid;
    const project = activeBid.project;
    const bidId = activeBid.id;
    const bidName = activeBid.name;
    const xodoHash = activeBid.xodo_document_hash;
    const status = activeBid.status;
    const file = activeBid.files?.[activeBid.files.length - 1];
    const signerCount = activeBid.signer_count;
    const updatedAt = activeBid.updated_at;
    const isTemplate = paramBid ? false : getIsTemplate();
    const readOnly = paramBid ? false : getReadOnly();
    const hasSentBills = activeBid.bills?.some((bill) => bill.sent);

    const menuOpen = Boolean(anchorEl);

    const handleFileUploaded = (newFiles) => {
        submitSigned();
        setFilesUploaderOpen(false);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleOpenTopSheetConfig = () => {
        handleMenuClose();
        openTopsheetConfig();
    };

    const handleOpenRequestForm = () => {
        handleMenuClose();
        openRequestForm();
    };

    const handleOpenFilesUploader = () => {
        setFilesUploaderOpen(true);
        handleMenuClose();
    };

    const handleAction = (action) => () => {
        setActionType(action);
        setActionOpen(true);
    };

    const handleCancel = () => {
        setCancelOpen(true);
    };
    const submitCancel = () => {
        request
            .post(`/bids/${bidId}/activity`, {
                action: constants.BID_ACTIVITY_CANCELLED,
                note: note || "Cancelled",
            })
            .then((response) => {
                setCancelOpen(false);
                setNote("");
            });
    };

    const submitSigned = () => {
        setConfirmingSigned(true);
        request
            .post(`/bids/${bidId}/activity`, {
                action: constants.BID_ACTIVITY_SIGNED,
                note: "Confirm client signed by uploading existing signature",
            })
            .then((response) => {
                setConfirmingSigned(false);
            });
    };

    const downloadForSignature = () => {
        setAnchorEl(null);
        request
            .post(`/bids/${bidId}/activity`, {
                action: constants.BID_ACTIVITY_DOWNLOADED,
                note: "Final version of topsheet downloaded for signature, bid sheet locked",
            })
            .then((response) => {
                downloadTopsheet(bidName);
            });
    };

    const getActionButtons = () => {
        const buttons = {
            topsheet: (
                <TopSheetButton
                    variant="startIcon"
                    onOpen={handleOpenTopSheetConfig}
                />
            ),
            signature: confirmingSigned ? (
                <CircularProgress />
            ) : (
                <Button
                    variant="contained"
                    startIcon={<Pen />}
                    onClick={handleMenuOpen}
                >
                    Signature
                </Button>
            ),
            cancel: (
                <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<CloseCircle />}
                    onClick={handleCancel}
                    data-testid="bid-header-cancel-button"
                >
                    Cancel
                </Button>
            ),
            pendingSignature: (
                <Button
                    variant="contained"
                    startIcon={xodoHash ? <ClockCircle /> : <Upload />}
                    onClick={handleOpenFilesUploader}
                    disabled={xodoHash}
                >
                    {xodoHash ? "Pending Signature" : "Upload Signed Topsheet"}
                </Button>
            ),
            signed: (
                <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Document />}
                    target="_blank"
                    href={file?.url}
                    disabled={!file}
                >
                    Signed Document
                </Button>
            ),
            sendForApproval: (
                <Button
                    variant="contained"
                    startIcon={<Pen size={18} />}
                    onClick={handleAction(constants.BID_ACTIVITY_SUBMITTED_EP)}
                    data-testid="bid-header-send-for-approval-button"
                >
                    Send for Approval
                </Button>
            ),
            pendingApproval: (
                <Button variant="contained" startIcon={<Restart />} disabled>
                    Approval
                </Button>
            ),
            approve: (
                <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={handleAction(constants.BID_ACTIVITY_APPROVED)}
                    data-testid="bid-header-approve-button"
                >
                    Approve
                </Button>
            ),
            reject: (
                <Button
                    color="error"
                    variant="contained"
                    startIcon={<CloseCircle />}
                    onClick={handleAction(constants.BID_ACTIVITY_REJECTED)}
                    data-testid="bid-header-reject-button"
                >
                    Reject
                </Button>
            ),
        };

        if (!canViewConfidentialData || isTemplate) {
            return [buttons.topsheet];
        }

        if (status == constants.BID_STATUS_DRAFT) {
            if (hasBidApprovalFlow) {
                return [buttons.topsheet, buttons.sendForApproval];
            } else {
                return [buttons.topsheet, buttons.signature];
            }
        } else if (status == constants.BID_STATUS_PENDING_EP) {
            if (canApproveBids) {
                return [buttons.topsheet, buttons.reject, buttons.approve];
            } else {
                return [
                    buttons.topsheet,
                    buttons.cancel,
                    buttons.pendingApproval,
                ];
            }
        } else if (status == constants.BID_STATUS_PENDING_FINANCE) {
            if (canApproveBids && isFinance) {
                return [buttons.topsheet, buttons.reject, buttons.approve];
            } else {
                return [
                    buttons.topsheet,
                    buttons.cancel,
                    buttons.pendingApproval,
                ];
            }
        } else if (status == constants.BID_STATUS_APPROVED) {
            return [buttons.topsheet, buttons.cancel, buttons.signature];
        } else if (status == constants.BID_STATUS_PENDING_SIGNATURE) {
            return [buttons.topsheet, buttons.cancel, buttons.pendingSignature];
        } else if (status == constants.BID_STATUS_SIGNED) {
            if (canApproveBids) {
                return [buttons.topsheet, buttons.cancel, buttons.signed];
            } else {
                return [buttons.topsheet, buttons.signed];
            }
        }

        return [];
    };

    const closeVersion = () => {
        setTimestampsOpen(false);
        getBid();
    };

    if (!bidName) {
        return null;
    }

    return (
        <Grid container direction="column" sx={{ margin: "24px 0 36px 0" }}>
            <Drawer
                variant="persistent"
                open={timestampsOpen}
                anchor="right"
                ModalProps={{ disableScrollLock: true }}
                PaperProps={{
                    sx: {
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                        border: "none",
                    },
                }}
            >
                <BidHistory
                    open={timestampsOpen}
                    getBid={getBid}
                    refreshBid={refreshBid}
                    close={closeVersion}
                />
            </Drawer>
            <Grid
                item
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginBottom: isMobile ? "12px" : "48px" }}
                rowSpacing={2}
            >
                <Grid item>
                    <BidTitle
                        bid={paramBid}
                        bidId={bidId}
                        refreshBid={refreshBid}
                    />

                    {project && (
                        <Grid
                            container
                            spacing={isMobile ? 1 : 3}
                            sx={{ paddingTop: "12px" }}
                        >
                            <Grid item>
                                <Grid container alignItems="center">
                                    <CaseMinimalistic />
                                    <IconText>{project.client.name}</IconText>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container alignItems="center">
                                    <Hashtag />
                                    <InternalLink
                                        href={`/projects/${project.id}`}
                                    >
                                        <IconText>{project.docket}</IconText>
                                    </InternalLink>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container alignItems="center">
                                    <Wallet />
                                    <IconText>{project.currency}</IconText>
                                </Grid>
                            </Grid>
                            <Grid item>
                                {hasFeature(FEATURE_MULTI_LEGAL_ENTITY) && (
                                    <Grid container alignItems="center">
                                        <Buildings />
                                        <IconText>
                                            {project.legal_entity.name}
                                        </IconText>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid item>
                                <Grid container alignItems="center">
                                    <UserRounded />
                                    <IconText>{`${project.owner.first_name} ${project.owner.last_name}`}</IconText>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>

                <Grid
                    item
                    container
                    justifyContent={isMobile ? "flex-start" : "flex-end"}
                    alignItems="center"
                    spacing={isMobile ? 1 : 3}
                    width="auto"
                >
                    {updatedAt && !readOnly && (
                        <Grid item>
                            <Tooltip
                                style={{ fontSize: "10px" }}
                                title={format(
                                    new Date(updatedAt + "Z"),
                                    "y-MM-dd h:mm aaa",
                                )}
                            >
                                <span>
                                    <Button
                                        onClick={() => setTimestampsOpen(true)}
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<History />}
                                    >
                                        <span>
                                            {formatDistanceToNow(
                                                new Date(updatedAt + "Z"),
                                            )}{" "}
                                            ago
                                        </span>
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    )}

                    {getActionButtons().map((button, i) => (
                        <Grid item key={i} sx={{ textAlign: "center" }}>
                            {button}
                        </Grid>
                    ))}

                    {isTemplate && canCreateGlobalTemplates && (
                        <Grid item>
                            {isBaseOrg ||
                            (activeBid.bid_template &&
                                activeBid.bid_template.public) ? (
                                <BidTemplatePublishingSwitch
                                    field="public"
                                    toggleField={(value) => !value}
                                    label="Public"
                                    disabled={!isBaseOrg}
                                />
                            ) : (
                                <BidTemplatePublishingSwitch
                                    field="person_id"
                                    toggleField={(value) =>
                                        value ? null : personId
                                    }
                                    label="Shared"
                                    invertBoolean
                                />
                            )}
                        </Grid>
                    )}
                </Grid>
            </Grid>

            {project && canViewConfidentialData && hasBidApprovalFlow && (
                <>
                    <BidInsights
                        currency={project.legal_entity.country.currency}
                        isRunning={isRunning}
                    />

                    <BidActionPopup
                        bidId={bidId}
                        action={actionType}
                        isFinance={isFinance}
                        open={actionOpen}
                        setOpen={setActionOpen}
                    />
                </>
            )}

            <ConfirmationDialog
                open={downloadForSignatureOpen}
                closeDialog={() => setDownloadForSignatureOpen(false)}
                title={`Download topsheet for signature`}
                callback={downloadForSignature}
            >
                <p>
                    You are about to download and lock the final version of your
                    bid sheet.
                </p>
            </ConfirmationDialog>

            {hasSentBills ? (
                <ConfirmationDialog
                    open={cancelOpen}
                    closeDialog={() => setCancelOpen(false)}
                    title={`Unable to revert to draft`}
                    hideOk
                >
                    <p style={{ marginTop: "0" }}>
                        This bid has one or more bills that have already been
                        marked as sent.
                    </p>
                </ConfirmationDialog>
            ) : (
                <ConfirmationDialog
                    open={cancelOpen}
                    closeDialog={() => setCancelOpen(false)}
                    title={`Are you sure?`}
                    callback={submitCancel}
                >
                    <p style={{ marginTop: "0" }}>
                        This will revert to a draft and require re-approval.
                    </p>

                    {status == constants.BID_STATUS_SIGNED && (
                        <>
                            <p>The following will also occur:</p>
                            <ul style={{ marginBottom: "32px" }}>
                                <li style={{ marginBottom: "6px" }}>
                                    bills will be unscheduled
                                </li>
                                <li style={{ marginBottom: "6px" }}>
                                    actualizations will be removed
                                </li>
                                <li style={{ marginBottom: "6px" }}>
                                    signed document PDF will be labelled as
                                    "archived"
                                </li>
                            </ul>

                            <TextField
                                label="Include note for finance..."
                                fullWidth
                                multiline
                                rows={3}
                                value={note}
                                onInput={(event) => setNote(event.target.value)}
                            />
                        </>
                    )}
                </ConfirmationDialog>
            )}

            {project && (
                <ConfirmationDialog
                    open={signedOpen}
                    closeDialog={() => setSignedOpen(false)}
                    title="Heads up..."
                    callback={submitSigned}
                >
                    Marking this bid as signed will automatically change the
                    status of
                    <br />
                    <strong>{project.name}</strong> to{" "}
                    <strong>In Production</strong>.
                </ConfirmationDialog>
            )}

            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                anchorOrigin={{ horizontal: "right", vertical: "top" }}
                transformOrigin={{ horizontal: "right", vertical: "bottom" }}
                onClose={handleMenuClose}
            >
                {hasFeature(FEATURE_BID_XODOSIGN) && (
                    <Tooltip
                        title={
                            signerCount == 0
                                ? "topsheet has no signer fields, you can adjust this in the topsheet configurator"
                                : ""
                        }
                        placement="left-end"
                    >
                        <MenuItem
                            onClick={handleOpenRequestForm}
                            disabled={signerCount == 0}
                        >
                            Request client signature
                        </MenuItem>
                    </Tooltip>
                )}
                {hasBidApprovalFlow && (
                    <MenuItem onClick={handleOpenFilesUploader}>
                        Upload signed topsheet
                    </MenuItem>
                )}
                {!hasBidApprovalFlow && (
                    <MenuItem
                        onClick={() => {
                            setDownloadForSignatureOpen(true);
                        }}
                    >
                        Download for Signature
                    </MenuItem>
                )}
            </Menu>

            <Modal
                open={filesUploaderOpen}
                onClose={() => setFilesUploaderOpen(false)}
            >
                <CustomBox
                    sx={{
                        background: theme.palette.background.paper,
                        borderRadius: 3,
                    }}
                >
                    <FilesUploader
                        files={[]}
                        modelId={bidId}
                        modelType={"bid"}
                        onUploadedCallback={handleFileUploaded}
                    />
                </CustomBox>
            </Modal>
        </Grid>
    );
}
